import { Separator } from "@dgs/core";
import { FieldArray, useField } from "formik";
import { FC, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import { ContactFilterItemFormValues } from "../shared/types";
import { ContactFilterAttributeSelect } from "./ContactFilterAttributeSelect";
import { ContactFilterEntry } from "./ContactFilterEntry";
import { maToEmptyFilterItemFormValues } from "./mappers";

const Divider = styled(Separator)<{ $title?: string | false }>`
	position: relative;
	margin-bottom: ${({ theme }) => theme.spacing(4)};
	${({ $title }) =>
		$title &&
		css`
			::after {
				position: absolute;
				display: block;
				left: 50%;
				top: 0;
				transform: translate(-50%, -50%);
				text-transform: uppercase;
				content: "${$title}";
				padding: 0 ${({ theme }) => theme.spacing(4)};
				color: ${({ theme }) => theme.colors.palette.grey[70]};
				background: ${({ theme }) => theme.colors.layout.content.background};
			}
		`}
`;

export const ContactFilterDrawerFilterItems: FC = () => {
	const { t } = useTranslation();
	const { contactAttributeOptions } = useContactAttributeOptions();
	const [filterItems] = useField<ContactFilterItemFormValues[]>("filterItems");

	const filteredOptions = useMemo(
		() =>
			contactAttributeOptions.filter(
				(option) => !filterItems.value.some((item) => item.contactAttributeId === option.id),
			),
		[contactAttributeOptions, filterItems.value],
	);

	return (
		<FieldArray
			name="filterItems"
			render={({ unshift, remove, form: { values }, name }) => (
				<>
					<ContactFilterAttributeSelect
						options={filteredOptions}
						label={t("Contact attribute")}
						onSelect={(option) => unshift(maToEmptyFilterItemFormValues(option.attribute))}
					/>
					{(values.filterItems as ContactFilterItemFormValues[]).map((_, index) => (
						<Fragment key={index}>
							<Divider $title={index > 0 && t("and")} />
							<ContactFilterEntry
								key={index}
								name={`${name}.${index}`}
								options={contactAttributeOptions}
								onDelete={() => remove(index)}
							/>
						</Fragment>
					))}
				</>
			)}
		/>
	);
};
